import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal/index';
import { Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { useLazyGetGoogleClassroomClassesQuery } from '../../features/authentication/authApiSlice';
import { useShareToGoogleClassroomMutation } from '../../features/authentication/assignmentApislice';
import { useCreateAssignmentLinkMutation } from '../../features/authentication/assignApiSlice';
import CustomToast from '../Toast';
import { useAssignmentContext } from '../../pages/assignments/context';
import Skeleton from 'react-loading-skeleton';

function Index({ showShareToGoogleClassroomModal, setShowShareToGoogleClassroomModal, urlGuid, assignmentName, isAssessment = false }) {
  const [getGoogleClassroomClasesCall] = useLazyGetGoogleClassroomClassesQuery();
  const { assignmentOptions } = useAssignmentContext();
  const assignmentTitleRef = useRef();
  const assignmentDescriptionRef = useRef();
  const assignmentClassRef = useRef();
  const [shareToGoogleClassroomCall] = useShareToGoogleClassroomMutation();
  const [createAssignmentLinkCall] = useCreateAssignmentLinkMutation();
  const [googleClasses, setGoogleClasses] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showTokenExpiredModel, setShowTokenExpiredModel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === 'googleAccessAllowed')
        getGoogleClassroomClasses();   
    };
    window.addEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (showShareToGoogleClassroomModal) {
      assignmentTitleRef.current.value = assignmentName;
      setIsLoading(true); 
      getGoogleClassroomClasses();    
    }
  }, [showShareToGoogleClassroomModal]);

  useEffect(() => {
    if (redirectUrl.length > 0 && showShareToGoogleClassroomModal) {
      window.open(redirectUrl, '_blank');
      setRedirectUrl('');
    }
  }, [redirectUrl]);

  const getGoogleClassroomClasses = ()=>{
    getGoogleClassroomClasesCall().then((results) => {
      if (results.error && results.error.data) {
        if (!results.error.data.success && results.error.data.tokenExpired) {
          setShowTokenExpiredModel(true);
          setIsLoading(false);
          return;
        }
      }
      else {
        if (results.data.redirectUrl) {
          setRedirectUrl(results.data.redirectUrl);
          return;
        }
        if (results.data.classes.length > 0) {
          setGoogleClasses(results.data.classes);
          setIsLoading(false);
        }
        else {
          setIsLoading(false);
          setShowShareToGoogleClassroomModal(false);
          CustomToast('No classes created in this linked Google Account', 'error');
        }
      }
    });
  };

  async function onShareToGoogleClassroom(e) {
    setIsFetching(true);
    e.preventDefault();
    let assignmentGuid = urlGuid; 

    if (!assignmentGuid) {
      try {
        const responseUrl = await createAssignmentLinkCall(assignmentOptions).unwrap();
        assignmentGuid = responseUrl[0];
      } catch {
        CustomToast('Error! Couldn\'t create assignment link', 'error');
        setIsFetching(false);
        return;
      }
    }
    try {
      assignmentGuid = assignmentGuid.toLowerCase().includes('classroomsso')? assignmentGuid : `${new URL(assignmentGuid).origin}/ClassroomSSO${new URL(assignmentGuid).search}`;
      var shareToGoogleResult = await shareToGoogleClassroomCall({ classId: assignmentClassRef.current.value.toString(), title: assignmentTitleRef.current.value, description: assignmentDescriptionRef.current.value.length > 0 ? assignmentDescriptionRef.current.value : '', url: assignmentGuid }).unwrap();
      if (shareToGoogleResult.success) {
        CustomToast(`${isAssessment ? 'Assessment' : 'Assignment'} shared to Google Classroom`, 'success');
      } else {
        CustomToast(shareToGoogleResult.reason, 'error');
      }
      setShowShareToGoogleClassroomModal(false);
     
    } catch {
      CustomToast('Error! Failed to share to Google Classroom', 'error');
    }
    setIsFetching(false);
  }

  return (
    <>
      <Modal
        contentWidth="900px"
        isOpen={showShareToGoogleClassroomModal}
        titleName={'Share to Google Classroom'}
        onClose={() => {
          setShowShareToGoogleClassroomModal(false);
        }}>
        {isLoading && 
          <>
            <div className='d-flex mb-3'>
              <div className='px-2'><Spinner animation="border" size="sm" /></div>
              <div>Authenticating with Google</div>
            </div>
            <div className="w-100 row mt-3">
              {[...Array(3)].map((_, index) => (
                <div className='col-12 mb-3' key={index}>
                  <Skeleton className='h-75' />
                </div>
              ))}
            </div>
          </>}

        <Form className={`d-flex flex-column ${isLoading && 'd-none'}`} onSubmit={!isFetching && onShareToGoogleClassroom}>
          <ListGroup.Item>
            <Form.Group id="assignmentClass">
              <Form.Label>Class</Form.Label>
              <div>
                <Form.Select ref={assignmentClassRef} required >
                  {googleClasses && googleClasses.map((classItem) => {
                    return (
                      <option key={`google-classroom-course${classItem.id}`} value={classItem.id}>{classItem.name}</option>
                    );
                  })}
                </Form.Select>
              </div>
            </Form.Group>
          </ListGroup.Item>

          <ListGroup.Item className='mt-3'>
            <Form.Group id="assignmentTitle">
              <Form.Label>Title</Form.Label>
              <div>
                <Form.Control
                  type='text'
                  ref={assignmentTitleRef}
                  required
                />
              </div>
            </Form.Group>
          </ListGroup.Item>
          <ListGroup.Item className='mt-3'>
            <Form.Group id="assignmentDescription">
              <Form.Label>Description</Form.Label>
              <div>
                <Form.Control
                  type='text'
                  ref={assignmentDescriptionRef}
                />
              </div>
            </Form.Group>
          </ListGroup.Item>
          <ListGroup.Item className='d-flex flex-row justify-content-end mt-4'>
            <Button type="submit" variant="castleRed" className='mx-3' disabled={isFetching}>{urlGuid ? 'Share To Google Classroom' : 'Create link and share to Google Classroom'}</Button>
            <Button variant="castleGray" onClick={() => { setShowShareToGoogleClassroomModal(false); }}>Cancel</Button>
          </ListGroup.Item>
        </Form>
      </Modal>
      <Modal
        contentWidth="700px"
        isOpen={showTokenExpiredModel}
        onClose={() =>{
          setShowTokenExpiredModel(false);
          setShowShareToGoogleClassroomModal(false);
        }
        }>
        <p>
          Your account is not authorized for Google Classroom access. Please check your Google Classroom account email and verify that is the one you are connecting with.
          On your Profile page click unlink from Google Classroom and try again.
        </p>
        <div className='container'>
          <div className="d-flex justify-content-center mt-3 gap-3 align-items-center">
            <button
              className="btn btn-castleRed print-btn"
              onClick={() =>{
                setShowTokenExpiredModel(false);
                setShowShareToGoogleClassroomModal(false);
              }
              }> OK
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

Index.propTypes = {
  showShareToGoogleClassroomModal: PropTypes.bool,
  setShowShareToGoogleClassroomModal: PropTypes.func,
  urlGuid: PropTypes.string,
  assignmentName: PropTypes.string,
  isAssessment: PropTypes.bool
};

export default Index;